import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import { currentPovState, povPerPointState } from 'store/krpanoStore'
import viewerLoadingState from 'store/viewerloadingState'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { BuildingFile, Point } from 'types/building'
import Krpano from 'utils/krpano'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import RotatedArrowIcon from 'components/icons/RotatedArrowIcon'

type Props = {
  point: Point
}

export default function ViewerPointImages({ point }: Props) {
  const { id: buildingId } = useParams()
  const [searchParams] = useSearchParams()
  const isAnnotationDisplay = searchParams.get('is_display')
  const subimageId = searchParams.get('subimage')
  const { id, image360, order } = point
  const { getBuiildingType } = useDirectory()
  const [isExpanded, setIsExpanded] = useState(false)
  const isFirstLoading = useRef<boolean>(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const subImageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const viewerLoading = useRecoilValue(viewerLoadingState)
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [povPerPoint, setPovPerPoint] = useRecoilState(povPerPointState)

  const { data: building, mutate: refetchBuilding } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${buildingId}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )

  const listSubImages = useMemo(
    () =>
      currentViewer.pointDetail?.id === point.id
        ? [...(currentViewer.pointDetail?.subImages || [])].sort(
            (a, b) => Number(a.order) - Number(b.order)
          )
        : [],
    [
      point.id,
      currentViewer.pointDetail?.id,
      currentViewer.pointDetail?.subImages
    ]
  )

  const directionList = useMemo(
    () =>
      currentViewer.pointDetail?.directions
        ?.map((item) => {
          const order = points.find(
            (p) =>
              p.id !== currentViewer.selectedPoint && item.id.includes(p.id)
          )?.order
          return !order ? { ...item, target: null } : { ...item, target: order }
        })
        .filter((item: any) => item.target),
    [currentViewer.pointDetail?.directions, currentViewer.selectedPoint, points]
  )

  const handleSelectPoint = (
    selectedImageFile?: BuildingFile,
    pointId?: string
  ) => {
    if (pointId && currentViewer.selectedPoint !== pointId) {
      // Before leaving, snapshot current camera into point memory
      const leavingPointId = currentViewer.pointDetail?.id
      const panoViewer = document.getElementById('embedpano-viewer') as any
      if (leavingPointId && panoViewer) {
        const h = Number(panoViewer.get('view.hlookat'))
        const v = Number(panoViewer.get('view.vlookat'))
        const z = Number(panoViewer.get('view.fov'))
        if (!Number.isNaN(h) && !Number.isNaN(v) && !Number.isNaN(z)) {
          setPovPerPoint((prev) => ({ ...prev, [leavingPointId]: { h, v, z } }))
        }
      }
      // Reset currentPov so it won't bleed into the next point
      setCurrentPov(null)

      setIsExpanded(false)
      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: pointId,
        selectedImage: selectedImageFile
          ? selectedImageFile.id
          : point.image360?.id,
        isClickToDirection: false,
        isSelectPointInLeftMenu: true
      }))
      return
    }
    if (selectedImageFile) {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: selectedImageFile.id
      }))
    } else {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: currentViewer.pointDetail?.image360?.id
      }))
    }

    // Update URL
    const urlToUpdate = new URL(window.location.href)
    urlToUpdate.searchParams.set('point', pointId || id)
    if (selectedImageFile && selectedImageFile.id !== point.image360?.id) {
      urlToUpdate.searchParams.set('subimage', selectedImageFile.id.toString())
    } else {
      urlToUpdate.searchParams.delete('subimage')
    }
    urlToUpdate.searchParams.delete('hlookat')
    urlToUpdate.searchParams.delete('vlookat')
    urlToUpdate.searchParams.delete('fov')
    window.history.pushState({}, '', urlToUpdate.toString())
  }

  const { trigger: getPointDetail } = useMutation(
    `v1/points/${currentViewer.selectedPoint}?building_id=${buildingId}&floor_id=${currentViewer.selectedFloor}`
  )
  useEffect(() => {
    const handlePointChange = async () => {
      if (
        currentViewer.isClickToDirection &&
        !currentViewer.isSelectPointInLeftMenu
      ) {
        setIsExpanded(false)
        return
      }
      //   if change point by click into menu left
      const panoViewer = document.getElementById('embedpano-viewer') as any
      if (!panoViewer) return
      const currentSelectedPoint = points.find(
        (p) => p.id === currentViewer.selectedPoint
      )
      if (id !== currentViewer.selectedPoint) return
      if (!panoViewer || !currentSelectedPoint) return

      const file =
        currentSelectedPoint.subImages?.find(
          (img) => img.id === currentViewer.selectedImage
        ) || currentSelectedPoint.image360
      if (!file) return
      const imageFile = { ...file }
      let annotations = currentSelectedPoint?.annotations || []
      const mainImage =
        currentViewer.selectedImage === currentSelectedPoint.image360?.id

      const data = await getPointDetail({
        method: 'get',
        data: null
      })

      const dataCurrentFile =
        data?.subImages?.find(
          (item: any) => item.id === currentViewer.selectedImage
        ) || data.image360
      if (dataCurrentFile) {
        imageFile.tilesUrl = dataCurrentFile.tilesUrl
        imageFile.multires = dataCurrentFile.multires
        imageFile.s3Path = dataCurrentFile.s3Path || ''
        imageFile.thumbnail = dataCurrentFile.thumbnailUrl || ''
        annotations = data?.annotations?.filter((item: any) => {
          if (item.imageID) {
            return item.imageID === currentViewer.selectedImage
          }
          return currentViewer.selectedImage === data.image360?.id
        })
      }

      // if change point with direction click return
      const isSamePoint = currentViewer.pointDetail?.id === data.id
      const isInternalNav = currentViewer.isClickToDirection
      const storedPov = povPerPoint[data.id]
      let pov

      const urlH = searchParams.get('hlookat')
      const urlV = searchParams.get('vlookat')
      const urlZ = searchParams.get('fov')

      // Allow POV preservation when switching Before & After images (same point) for all building types
      if (isSamePoint && currentPov) {
        pov = currentPov
      } else if (isInternalNav && currentPov) {
        pov = currentPov
      } else if (urlH && urlV && urlZ && isFirstLoading.current) {
        pov = { h: +urlH, v: +urlV, z: +urlZ }
      } else if (storedPov && !currentViewer.isSelectPointInLeftMenu) {
        // v1.4: Fix POV reset when clicking Left Menu in Preview Mode
        // Ignore storedPov if navigation is triggered by Left Menu to enforce Strict Reset
        pov = storedPov
      } else {
        pov = mainImage
          ? currentSelectedPoint.initView ||
            dataCurrentFile?.initView ||
            data?.initView
          : dataCurrentFile?.initView ||
            currentSelectedPoint.initView ||
            data?.initView
      }

      if (!pov) return
      const { h, v, z } = pov

      const tilesUrl = imageFile.tilesUrl || ''
      const previewUrl = imageFile.thumbnail

      const { isLocked } = currentSelectedPoint
      const { multires } = imageFile
      const imageUrl = imageFile.s3Path

      // Always re-add all annotations, directions, stairways after loading any image
      // const annotations = currentSelectedPoint?.annotations || []
      const directions = directionList
      const stairways = data.stairways?.map((item: any) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name
      }))
      const sceneName = Krpano.getScenceNameByFileId(
        currentViewer.selectedPoint,
        points,
        currentViewer.selectedImage
      )

      if (panoViewer.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: tilesUrl || undefined,
          multires: multires || undefined,
          s3Path: imageUrl,
          thumbnailUrl: previewUrl || ''
        })
        panoViewer.call(`loadxml(${screenString}, null, MERGE);`)
      }

      panoViewer.call(`load_scene(${sceneName},${h},${v},${z})`)
      isFirstLoading.current = false

      !viewerLoading.firstLoading &&
        annotations &&
        Krpano.AddAnnotation(
          panoViewer,
          annotations,
          isAnnotationDisplay,
          false,
          false
        )
      directions && Krpano.AddDirectionArrow(panoViewer, data.directions)
      stairways && Krpano.AddStairway(panoViewer, stairways)
      Krpano.TogglePointLock(isLocked)
    }
    // avoid useEffect multiple times in other point
    if (id !== currentViewer.selectedPoint) {
      return
    }
    handlePointChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedImage])

  useEffect(() => {
    if (
      subimageId &&
      id === currentViewer.selectedPoint &&
      point.isSubImages &&
      !isExpanded
    ) {
      setIsExpanded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subimageId, id, currentViewer.selectedPoint])

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (
      !currentViewer.selectedPoint ||
      currentViewer.selectedPoint !== point.id
    ) {
      setCurrentViewer((prev) => ({ ...prev, selectedPoint: point.id }))
    }
    setIsExpanded((prev) => !prev)
  }

  useEffect(() => {
    if (isExpanded) {
      setCurrentViewer((prev) => ({
        ...prev,
        isSelectPointInLeftMenu: true,
        isClickToDirection: false
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded])

  // v1.2 - 27/03/2026 - Auto expand subimages if selected image is a subimage of this point
  useEffect(() => {
    if (
      currentViewer.selectedPoint === id &&
      currentViewer.selectedImage &&
      listSubImages.some((img) => img.id === currentViewer.selectedImage)
    ) {
      setIsExpanded(true)
    }
  }, [
    id,
    currentViewer.selectedPoint,
    currentViewer.selectedImage,
    listSubImages
  ])

  useEffect(() => {
    if (currentViewer.selectedPoint === id) {
      if (currentViewer.selectedImage === image360?.id && scrollRef.current) {
        scrollRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      } else if (
        isExpanded &&
        currentViewer.selectedImage &&
        subImageRefs.current[currentViewer.selectedImage]
      ) {
        subImageRefs.current[currentViewer.selectedImage]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }
    }
  }, [
    currentViewer.selectedPoint,
    currentViewer.selectedImage,
    id,
    isExpanded,
    image360?.id
  ])

  useEffect(() => {
    if (currentViewer.selectedPoint && id !== currentViewer.selectedPoint) {
      return
    }
    refetchBuilding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedFloor, refetchBuilding])

  return (
    <>
      <div
        ref={scrollRef}
        className={clsx('p-[10px] min-h-[36px] relative', {
          'theme-bg-sub-primary':
            currentViewer.selectedPoint === id &&
            currentViewer.selectedImage === image360?.id
        })}
        onClick={() => handleSelectPoint(undefined, point.id)}
      >
        <div
          className={`absolute ${
            image360 ? 'top-[15px] left-[16px]' : 'top-[10px] left-[10px]'
          } z-10 rounded-lg flex bg-[#021120] p-[2px] min-w-[16px]`}
        >
          <div className="m-auto text-[13px] leading-[13px] font-bold text-white">
            {order}
          </div>
        </div>

        {point.isSubImages && (
          <div
            className="absolute top-[15px] left-[35px] z-10 rounded-full flex bg-[#787B82CC] w-[18px] h-[18px]"
            onClick={handleToggle}
          >
            <RotatedArrowIcon className="m-auto" />
          </div>
        )}

        {image360 && (
          <>
            <LazyThumbnail
              imageType={ImageType.OBJECT_FILL}
              url={
                image360.multires
                  ? `${image360.tilesUrl}thumb.jpg`
                  : image360.thumbnail
              }
              name={image360.name}
            />
            {image360 && !image360?.note ? (
              <div className="ant-typography text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text className="text-[12px] text-white">
                  {dayjs(image360?.takenAt)
                    ?.utc()
                    .format('YYYY-MM-DD HH:mm')}
                </Typography.Text>
              </div>
            ) : (
              <div className="ant-typography text-white text-[12px] max-w-[100px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text
                  className="text-[12px] text-white truncate"
                  ellipsis={{ tooltip: image360?.note }}
                >
                  {image360?.note}
                </Typography.Text>
              </div>
            )}
          </>
        )}
      </div>
      {isExpanded &&
        listSubImages.map((item) => (
          <div
            key={item.id}
            ref={(el) => {
              subImageRefs.current[item.id] = el
            }}
            className={clsx('p-[10px] min-h-[36px] relative', {
              'theme-bg-sub-primary': currentViewer.selectedImage === item.id
            })}
            onClick={() => handleSelectPoint(item, point.id)}
          >
            <LazyThumbnail
              imageType={ImageType.OBJECT_FILL}
              url={item.multires ? `${item.tilesUrl}thumb.jpg` : item.thumbnail}
              name={item.name}
            />
            {item && !item?.note ? (
              <div className="ant-typography text-white text-[12px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text className="text-[12px] text-white">
                  {dayjs(item?.takenAt)
                    ?.utc()
                    .format('YYYY-MM-DD HH:mm')}
                </Typography.Text>
              </div>
            ) : (
              <div className="ant-typography text-white text-[12px] max-w-[180px] bg-[#02112070] absolute right-[10px] bottom-[10px] px-1 leading-none flex items-center">
                <Typography.Text
                  className="text-[12px] text-white truncate"
                  ellipsis={{ tooltip: item?.note }}
                >
                  {item?.note}
                </Typography.Text>
              </div>
            )}
          </div>
        ))}
    </>
  )
}
